import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  ClipboardCheck,
  Camera,
  FileText,
  Download,
  Loader2,
  Plus,
  X,
  ExternalLink,
} from "lucide-react";

const INSPECTION_TYPES = [
  { id: "phytosanitary", label: "Phytosanitary Inspection", color: "#65a30d" },
  { id: "customs_physical", label: "Customs Physical Check", color: "#dc2626" },
  { id: "veterinary", label: "Veterinary Inspection", color: "#16a34a" },
  { id: "cargo_damage", label: "Cargo Damage Inspection", color: "#ea580c" },
];

const RESULTS = [
  { id: "pass", label: "Pass ✓", color: "#16a34a" },
  { id: "pending", label: "Pending ⏳", color: "#d97706" },
  { id: "fail", label: "Fail ✗", color: "#dc2626" },
];

const resultBadge = (r) => {
  if (r === "pass") return { txt: "✓ PASSED", cls: "bg-green-100 text-green-700" };
  if (r === "fail") return { txt: "✗ FAILED", cls: "bg-red-100 text-red-700" };
  if (r === "pending") return { txt: "⏳ PENDING", cls: "bg-amber-100 text-amber-700" };
  return { txt: (r || "").toUpperCase(), cls: "bg-slate-100 text-slate-600" };
};

const typeLabel = (t) =>
  INSPECTION_TYPES.find((x) => x.id === t)?.label || (t || "").replace(/_/g, " ");

export function InspectionReportsPanel({ pfId, pfNumber, token, onUpdate }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    inspection_type: "phytosanitary",
    result: "",
    inspector_name: "",
    inspection_date: "",
    location: "",
    notes: "",
    notify_customer: true,
  });
  const [photos, setPhotos] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/position-folders/${pfId}/inspection-reports`, { headers });
      setReports(r.data.reports || []);
    } catch (e) {
      /* none */
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfId, token]);

  useEffect(() => {
    if (pfId) fetchReports();
  }, [pfId, fetchReports]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      if (!f.type.startsWith("image/")) return;
      setPhotos((prev) => [...prev, { id: Date.now() + Math.random(), file: f, preview: URL.createObjectURL(f) }]);
    });
    e.target.value = "";
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const resetForm = () => {
    setForm({
      inspection_type: "phytosanitary", result: "", inspector_name: "",
      inspection_date: "", location: "", notes: "", notify_customer: true,
    });
    setPhotos([]);
  };

  const submit = async () => {
    if (!["pass", "fail", "pending"].includes(form.result)) {
      toast.error("Choose a result: Pass, Pending or Fail");
      return;
    }
    if (!form.notes.trim()) {
      toast.error("Please add inspection notes");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("inspection_type", form.inspection_type);
      fd.append("result", form.result);
      fd.append("notes", form.notes);
      fd.append("location", form.location || "");
      fd.append("inspector_name", form.inspector_name || "");
      if (form.inspection_date) fd.append("inspection_date", new Date(form.inspection_date).toISOString());
      fd.append("notify_customer", form.notify_customer ? "true" : "false");
      photos.forEach((p) => fd.append("files", p.file, p.file.name || "photo.jpg"));
      const r = await axios.post(
        `${API_URL}/position-folders/${pfId}/inspection-report`,
        fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        `Report saved (${r.data.photo_count} photos)` +
          (r.data.customer_email_sent ? " — emailed to customer ✓" : "")
      );
      setShowDialog(false);
      resetForm();
      fetchReports();
      onUpdate && onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="bg-white rounded-xl border border-teal-200 p-4"
      data-testid="inspection-reports-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-teal-900 flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            Inspection reports
          </div>
          <p className="text-xs text-slate-500">
            Phytosanitary / Customs / Veterinary — make a report with photos, view &amp; download as PDF.
          </p>
        </div>
        <Button
          size="sm"
          className="bg-teal-600 hover:bg-teal-700 text-white"
          onClick={() => setShowDialog(true)}
          data-testid="make-inspection-report-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Make report
        </Button>
      </div>

      {loading ? (
        <div className="py-4 text-center text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
        </div>
      ) : reports.length === 0 ? (
        <p className="text-sm text-slate-400 py-2">No inspection reports yet.</p>
      ) : (
        <div className="space-y-3">
          {reports.map((rep) => {
            const b = resultBadge(rep.result);
            const viewUrl = `/inspection/${rep.id}/${rep.view_token}`;
            const pdfUrl = `${API_URL}/inspection/${rep.id}/${rep.view_token}/pdf`;
            const pics = rep.photos || [];
            return (
              <div
                key={rep.id}
                className="border rounded-lg p-3"
                data-testid={`inspection-report-${rep.id}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-slate-800">
                      {typeLabel(rep.inspection_type)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {rep.scheduled_date ? new Date(rep.scheduled_date).toLocaleString() : ""}
                      {rep.inspector_name ? ` • ${rep.inspector_name}` : ""}
                      {` • ${rep.photo_count || pics.length} photo(s)`}
                    </p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${b.cls}`}>
                    {b.txt}
                  </span>
                </div>

                {pics.length > 0 && (
                  <div className="flex gap-2 mt-2 overflow-x-auto">
                    {pics.slice(0, 8).map((p, i) => (
                      <a
                        key={p.id || i}
                        href={`${API_BASE_URL}${p.url || `/api/inspection-photo/${p.grid_id}`}`}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0"
                      >
                        <img
                          src={`${API_BASE_URL}${p.url || `/api/inspection-photo/${p.grid_id}`}`}
                          alt=""
                          className="w-16 h-16 object-cover rounded-md border"
                        />
                      </a>
                    ))}
                    {pics.length > 8 && (
                      <span className="text-xs text-slate-400 self-center">+{pics.length - 8}</span>
                    )}
                  </div>
                )}

                {rep.notes && (
                  <p className="text-xs text-slate-600 mt-2 whitespace-pre-wrap line-clamp-3">{rep.notes}</p>
                )}

                <div className="flex gap-2 mt-3">
                  <a href={viewUrl} target="_blank" rel="noreferrer" data-testid={`insp-report-view-${rep.id}`}>
                    <Button size="sm" variant="outline">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open report
                    </Button>
                  </a>
                  <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid={`insp-report-pdf-${rep.id}`}>
                    <Button size="sm" variant="outline">
                      <Download className="w-3.5 h-3.5 mr-1" /> PDF
                    </Button>
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Make inspection report — {pfNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Inspection type</label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={form.inspection_type}
                onChange={(e) => setForm({ ...form, inspection_type: e.target.value })}
                data-testid="insp-report-type-select"
              >
                {INSPECTION_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-slate-600 mb-1 block">
                Result <span className="text-red-500">*</span>
                <span className="block text-xs text-slate-400">Pending = send photos now, file final Pass/Fail later</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                {RESULTS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setForm({ ...form, result: r.id })}
                    className={`py-2 rounded-lg text-sm font-medium border-2 ${
                      form.result === r.id ? "text-white" : "bg-white text-slate-700 border-slate-200"
                    }`}
                    style={{
                      backgroundColor: form.result === r.id ? r.color : undefined,
                      borderColor: form.result === r.id ? r.color : undefined,
                    }}
                    data-testid={`insp-report-result-${r.id}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Inspector (officer)</label>
                <Input
                  value={form.inspector_name}
                  onChange={(e) => setForm({ ...form, inspector_name: e.target.value })}
                  placeholder="e.g., Franko Babic"
                  data-testid="insp-report-inspector"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Inspection date</label>
                <Input
                  type="datetime-local"
                  value={form.inspection_date}
                  onChange={(e) => setForm({ ...form, inspection_date: e.target.value })}
                  data-testid="insp-report-date"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-600 mb-1 block">Location</label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g., Port of Koper"
              />
            </div>

            <div>
              <label className="text-sm text-slate-600 mb-1 block">Notes <span className="text-red-500">*</span></label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Describe what was checked…"
                data-testid="insp-report-notes"
              />
            </div>

            <div>
              <label className="text-sm text-slate-600 mb-2 block">Photos ({photos.length})</label>
              {photos.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {photos.map((p) => (
                    <div key={p.id} className="relative aspect-square">
                      <img src={p.preview} alt="" className="w-full h-full object-cover rounded-md" />
                      <button
                        type="button"
                        onClick={() => removePhoto(p.id)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="inline-flex items-center gap-2 border rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                <Camera className="w-4 h-4" /> Add photos
                <input type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="insp-report-photos-input" />
              </label>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.notify_customer}
                onChange={(e) => setForm({ ...form, notify_customer: e.target.checked })}
                data-testid="insp-report-notify"
              />
              Email report + photo link to the customer
            </label>

            <Button
              className="w-full bg-teal-600 hover:bg-teal-700 text-white"
              onClick={submit}
              disabled={submitting || !["pass", "fail", "pending"].includes(form.result) || !form.notes.trim()}
              data-testid="insp-report-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              Save report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default InspectionReportsPanel;
